/**
 * Raptor Predator Frontend - Threat Store
 * 
 * Manages threat data, aggregated threats, and remediation state.
 * Designed for real-time updates via WebSocket with frame-locked processing.
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  Threat,
  ThreatType,
  ThreatSeverity,
  ThreatStatus,
  AggregatedThreat,
  RemediationPlan,
  RemediationAction,
  Inoculation,
} from '../types';

// ============================================================================
// Types
// ============================================================================

interface ThreatFilters {
  severities: ThreatSeverity[];
  types: ThreatType[];
  statuses: ThreatStatus[];
  ldcIds: string[];
  searchQuery: string;
}

interface ThreatStats {
  total: number;
  bySeverity: Record<ThreatSeverity, number>;
  byType: Record<ThreatType, number>;
  byStatus: Record<ThreatStatus, number>;
  activeCount: number;
  resolvedToday: number;
}

interface ThreatState {
  // Individual threats
  threats: Map<string, Threat>;
  
  // Aggregated threats (grouped by type/indicator)
  aggregatedThreats: Map<string, AggregatedThreat>;
  
  // Active remediations
  activeRemediations: Map<string, RemediationPlan>;
  
  // Inoculations
  inoculations: Map<string, Inoculation>;
  
  // Filters and search
  filters: ThreatFilters;
  
  // Stats
  stats: ThreatStats;
  
  // Selected state
  selectedThreatId: string | null;
  selectedAggregatedThreatId: string | null;
  
  // Pending updates (for frame-locked processing)
  pendingUpdates: {
    type: 'add' | 'update' | 'remove';
    threat: Partial<Threat> & { id: string };
  }[];
  
  // Loading state
  isLoading: boolean;
  error: string | null;
  lastUpdate: number;
}

interface ThreatActions {
  // Threat operations
  addThreat: (threat: Threat) => void;
  updateThreat: (id: string, updates: Partial<Threat>) => void;
  removeThreat: (id: string) => void;
  setThreats: (threats: Threat[]) => void;
  
  // Aggregation
  aggregateThreats: () => void;
  
  // Remediation
  startRemediation: (threatId: string, plan: RemediationPlan) => void;
  updateRemediationAction: (planId: string, actionId: string, updates: Partial<RemediationAction>) => void;
  completeRemediation: (planId: string) => void;
  
  // Inoculation
  startInoculation: (inoculation: Inoculation) => void;
  updateInoculation: (id: string, updates: Partial<Inoculation>) => void;
  
  // Selection
  selectThreat: (id: string | null) => void;
  selectAggregatedThreat: (id: string | null) => void;
  
  // Filtering
  setFilters: (filters: Partial<ThreatFilters>) => void;
  resetFilters: () => void;
  
  // Frame-locked updates (for socket events)
  queueUpdate: (update: ThreatState['pendingUpdates'][0]) => void;
  processPendingUpdates: (maxUpdates?: number) => number;
  
  // State management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: ThreatFilters = {
  severities: [],
  types: [],
  statuses: [],
  ldcIds: [],
  searchQuery: '',
};

const initialStats: ThreatStats = {
  total: 0,
  bySeverity: {} as Record<ThreatSeverity, number>,
  byType: {} as Record<ThreatType, number>,
  byStatus: {} as Record<ThreatStatus, number>,
  activeCount: 0,
  resolvedToday: 0,
};

const initialState: ThreatState = {
  threats: new Map(),
  aggregatedThreats: new Map(),
  activeRemediations: new Map(),
  inoculations: new Map(),
  filters: initialFilters,
  stats: initialStats,
  selectedThreatId: null,
  selectedAggregatedThreatId: null,
  pendingUpdates: [],
  isLoading: false,
  error: null,
  lastUpdate: Date.now(),
};

// ============================================================================
// Helper Functions
// ============================================================================

function computeStats(threats: Map<string, Threat>): ThreatStats {
  const stats: ThreatStats = {
    total: threats.size,
    bySeverity: {} as Record<ThreatSeverity, number>,
    byType: {} as Record<ThreatType, number>,
    byStatus: {} as Record<ThreatStatus, number>,
    activeCount: 0,
    resolvedToday: 0,
  };
  
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  
  for (const threat of threats.values()) {
    // By severity
    stats.bySeverity[threat.severity] = (stats.bySeverity[threat.severity] || 0) + 1;
    
    // By type
    stats.byType[threat.type] = (stats.byType[threat.type] || 0) + 1;
    
    // By status
    stats.byStatus[threat.status] = (stats.byStatus[threat.status] || 0) + 1;
    
    // Active count
    if (threat.status === 'active' || threat.status === 'investigating') {
      stats.activeCount++;
    }
    
    // Resolved today
    if (threat.resolvedAt && new Date(threat.resolvedAt) >= todayStart) {
      stats.resolvedToday++;
    }
  }
  
  return stats;
}

function aggregateThreatsInternal(threats: Map<string, Threat>): Map<string, AggregatedThreat> {
  const aggregated = new Map<string, AggregatedThreat>();
  
  // Group by threat type
  const byType = new Map<ThreatType, Threat[]>();
  for (const threat of threats.values()) {
    if (threat.status === 'active' || threat.status === 'investigating') {
      const list = byType.get(threat.type) || [];
      list.push(threat);
      byType.set(threat.type, list);
    }
  }
  
  // Create aggregated threats
  for (const [type, typeThreats] of byType) {
    if (typeThreats.length > 1) {
      // Find max severity
      const severityOrder: ThreatSeverity[] = ['critical', 'high', 'medium', 'low', 'info'];
      let maxSeverity: ThreatSeverity = 'info';
      for (const threat of typeThreats) {
        if (severityOrder.indexOf(threat.severity) < severityOrder.indexOf(maxSeverity)) {
          maxSeverity = threat.severity;
        }
      }
      
      // Collect common indicators
      const indicatorMap = new Map<string, number>();
      for (const threat of typeThreats) {
        for (const indicator of threat.indicators) {
          const key = `${indicator.type}:${indicator.value}`;
          indicatorMap.set(key, (indicatorMap.get(key) || 0) + 1);
        }
      }
      
      // Get common indicators (appearing in >50% of threats)
      const threshold = Math.ceil(typeThreats.length / 2);
      const commonIndicators = typeThreats[0]?.indicators.filter(ind => {
        const key = `${ind.type}:${ind.value}`;
        return (indicatorMap.get(key) || 0) >= threshold;
      }) || [];
      
      const aggThreat: AggregatedThreat = {
        id: `agg-${type}`,
        threatType: type,
        severity: maxSeverity,
        deviceCount: new Set(typeThreats.flatMap(t => t.affectedDevices)).size,
        deviceIds: [...new Set(typeThreats.flatMap(t => t.affectedDevices))],
        firstDetected: new Date(Math.min(...typeThreats.map(t => new Date(t.detectedAt).getTime()))),
        lastDetected: new Date(Math.max(...typeThreats.map(t => new Date(t.detectedAt).getTime()))),
        commonIndicators,
        status: 'active',
      };
      
      aggregated.set(aggThreat.id, aggThreat);
    }
  }
  
  return aggregated;
}

// ============================================================================
// Store Creation
// ============================================================================

export const useThreatStore = create<ThreatState & ThreatActions>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,
        
        // Threat operations
        addThreat: (threat) => { set((state) => {
          state.threats.set(threat.id, threat);
          state.stats = computeStats(state.threats);
          state.aggregatedThreats = aggregateThreatsInternal(state.threats);
          state.lastUpdate = Date.now();
        }); },
        
        updateThreat: (id, updates) => { set((state) => {
          const threat = state.threats.get(id);
          if (threat) {
            Object.assign(threat, updates);
            state.stats = computeStats(state.threats);
            state.aggregatedThreats = aggregateThreatsInternal(state.threats);
            state.lastUpdate = Date.now();
          }
        }); },
        
        removeThreat: (id) => { set((state) => {
          state.threats.delete(id);
          state.stats = computeStats(state.threats);
          state.aggregatedThreats = aggregateThreatsInternal(state.threats);
          state.lastUpdate = Date.now();
        }); },
        
        setThreats: (threats) => { set((state) => {
          state.threats.clear();
          for (const threat of threats) {
            state.threats.set(threat.id, threat);
          }
          state.stats = computeStats(state.threats);
          state.aggregatedThreats = aggregateThreatsInternal(state.threats);
          state.lastUpdate = Date.now();
        }); },
        
        // Aggregation
        aggregateThreats: () => { set((state) => {
          state.aggregatedThreats = aggregateThreatsInternal(state.threats);
        }); },
        
        // Remediation
        startRemediation: (threatId, plan) => { set((state) => {
          state.activeRemediations.set(plan.id, plan);
          const threat = state.threats.get(threatId);
          if (threat) {
            threat.remediationPlan = plan;
            threat.status = 'contained';
          }
          state.stats = computeStats(state.threats);
          state.lastUpdate = Date.now();
        }); },
        
        updateRemediationAction: (planId, actionId, updates) => { set((state) => {
          const plan = state.activeRemediations.get(planId);
          if (plan) {
            const action = plan.actions.find(a => a.id === actionId);
            if (action) {
              Object.assign(action, updates);
            }
          }
          state.lastUpdate = Date.now();
        }); },
        
        completeRemediation: (planId) => { set((state) => {
          state.activeRemediations.delete(planId);
          
          // Find and update associated threat
          for (const threat of state.threats.values()) {
            if (threat.remediationPlan?.id === planId) {
              threat.status = 'remediated';
              threat.resolvedAt = new Date();
              break;
            }
          }
          state.stats = computeStats(state.threats);
          state.aggregatedThreats = aggregateThreatsInternal(state.threats);
          state.lastUpdate = Date.now();
        }); },
        
        // Inoculation
        startInoculation: (inoculation) => { set((state) => {
          state.inoculations.set(inoculation.id, inoculation);
          state.lastUpdate = Date.now();
        }); },
        
        updateInoculation: (id, updates) => { set((state) => {
          const inoculation = state.inoculations.get(id);
          if (inoculation) {
            Object.assign(inoculation, updates);
          }
          state.lastUpdate = Date.now();
        }); },
        
        // Selection
        selectThreat: (id) => { set((state) => {
          state.selectedThreatId = id;
        }); },
        
        selectAggregatedThreat: (id) => { set((state) => {
          state.selectedAggregatedThreatId = id;
        }); },
        
        // Filtering
        setFilters: (filters) => { set((state) => {
          Object.assign(state.filters, filters);
        }); },
        
        resetFilters: () => { set((state) => {
          state.filters = initialFilters;
        }); },
        
        // Frame-locked updates
        queueUpdate: (update) => { set((state) => {
          state.pendingUpdates.push(update);
        }); },
        
        processPendingUpdates: (maxUpdates = 10) => {
          const state = get();
          const toProcess = state.pendingUpdates.slice(0, maxUpdates);
          
          if (toProcess.length === 0) {
            return 0;
          }
          
          set((draft) => {
            draft.pendingUpdates = draft.pendingUpdates.slice(maxUpdates);
            
            for (const update of toProcess) {
              switch (update.type) {
                case 'add':
                  draft.threats.set(update.threat.id, update.threat as Threat);
                  break;
                case 'update': {
                  const existing = draft.threats.get(update.threat.id);
                  if (existing) {
                    Object.assign(existing, update.threat);
                  }
                  break;
                }
                case 'remove':
                  draft.threats.delete(update.threat.id);
                  break;
              }
            }
            
            draft.stats = computeStats(draft.threats);
            draft.aggregatedThreats = aggregateThreatsInternal(draft.threats);
            draft.lastUpdate = Date.now();
          });
          
          return toProcess.length;
        },
        
        // State management
        setLoading: (loading) => { set((state) => {
          state.isLoading = loading;
        }); },
        
        setError: (error) => { set((state) => {
          state.error = error;
          state.isLoading = false;
        }); },
      }))
    ),
    { name: 'threat-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectActiveThreats = (state: ThreatState & ThreatActions) => {
  const result: Threat[] = [];
  for (const threat of state.threats.values()) {
    if (threat.status === 'active' || threat.status === 'investigating') {
      result.push(threat);
    }
  }
  return result;
};

export const selectThreatStats = (state: ThreatState & ThreatActions) => state.stats;
export const selectAggregatedThreats = (state: ThreatState & ThreatActions) => state.aggregatedThreats;
export const selectSelectedThreat = (state: ThreatState & ThreatActions) => 
  state.selectedThreatId ? state.threats.get(state.selectedThreatId) : null;
export const selectPendingUpdatesCount = (state: ThreatState & ThreatActions) => state.pendingUpdates.length;
export const selectActiveRemediations = (state: ThreatState & ThreatActions) => state.activeRemediations;
export const selectInoculations = (state: ThreatState & ThreatActions) => state.inoculations;

// ============================================================================
// Filtered Threats Selector
// ============================================================================

export const selectFilteredThreats = (state: ThreatState & ThreatActions): Threat[] => {
  const { threats, filters } = state;
  const result: Threat[] = [];
  
  for (const threat of threats.values()) {
    // Severity filter
    if (filters.severities.length > 0 && !filters.severities.includes(threat.severity)) {
      continue;
    }
    
    // Type filter
    if (filters.types.length > 0 && !filters.types.includes(threat.type)) {
      continue;
    }
    
    // Status filter
    if (filters.statuses.length > 0 && !filters.statuses.includes(threat.status)) {
      continue;
    }
    
    // LDC filter
    if (filters.ldcIds.length > 0 && !filters.ldcIds.includes(threat.source.ldcId)) {
      continue;
    }
    
    // Search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matchesDescription = threat.description.toLowerCase().includes(query);
      const matchesType = threat.type.toLowerCase().includes(query);
      const matchesDevice = threat.source.deviceName.toLowerCase().includes(query);
      
      if (!matchesDescription && !matchesType && !matchesDevice) {
        continue;
      }
    }
    
    result.push(threat);
  }
  
  return result;
};
